import { authStorageKeys, impersonationStorageKeys } from './sessionStorage'

// Режим просмотра (impersonation): суперадмин-сессия и imp-флаг хранятся в
// localStorage — токен тоже в localStorage и шарится между вкладками, поэтому
// новая вкладка должна видеть режим просмотра, а не разлогинивать суперадмина.
// Refresh-токена у imp-токена нет.

export interface ImpersonationState {
  organization_id: string
  organization_name: string
  expires_at: number
  return_path?: string
}

interface AdminSessionBackup {
  accessToken: string | null
  refreshToken: string | null
  expiresAt: string | null
  user: string | null
  activeOrganizationId: string | null
}

const IMPERSONATION_KEY = impersonationStorageKeys.state
const ADMIN_BACKUP_KEY = impersonationStorageKeys.adminBackup

export const getImpersonationState = (): ImpersonationState | null => {
  const raw = localStorage.getItem(IMPERSONATION_KEY)
  if (!raw) {
    return null
  }
  try {
    return JSON.parse(raw) as ImpersonationState
  } catch {
    localStorage.removeItem(IMPERSONATION_KEY)
    return null
  }
}

export const isImpersonationActive = (): boolean => getImpersonationState() !== null

// Браузеры нормализуют «\» в «/» при навигации, поэтому «/\evil.com»
// эквивалентен protocol-relative «//evil.com» — бэкслеши тоже блокируем.
export const sanitizeReturnPath = (path: string): string | null => {
  if (!path.startsWith('/') || path.startsWith('//') || path.includes('\\')) {
    return null
  }
  return path
}

export const getImpersonationReturnPath = (): string => {
  const state = getImpersonationState()
  if (state?.return_path) {
    const safe = sanitizeReturnPath(state.return_path)
    if (safe) {
      return safe
    }
  }
  if (state?.organization_id) {
    return `/admin/organizations/${state.organization_id}`
  }
  return '/admin/organizations'
}

export const saveAdminSessionBackup = (): void => {
  const backup: AdminSessionBackup = {
    accessToken: localStorage.getItem(authStorageKeys.accessToken),
    refreshToken: localStorage.getItem(authStorageKeys.refreshToken),
    expiresAt: localStorage.getItem(authStorageKeys.expiresAt),
    user: localStorage.getItem(authStorageKeys.user),
    activeOrganizationId: localStorage.getItem(authStorageKeys.activeOrganizationId),
  }
  localStorage.setItem(ADMIN_BACKUP_KEY, JSON.stringify(backup))
}

export const setImpersonationState = (state: ImpersonationState): void => {
  localStorage.setItem(IMPERSONATION_KEY, JSON.stringify(state))
}

const applyBackupValue = (key: string, value: string | null): void => {
  if (value === null) {
    localStorage.removeItem(key)
    return
  }
  localStorage.setItem(key, value)
}

// restoreAdminSession возвращает суперадмин-сессию из бэкапа в активные ключи.
export const restoreAdminSession = (): boolean => {
  const raw = localStorage.getItem(ADMIN_BACKUP_KEY)
  localStorage.removeItem(IMPERSONATION_KEY)
  localStorage.removeItem(ADMIN_BACKUP_KEY)
  if (!raw) {
    return false
  }

  try {
    const backup = JSON.parse(raw) as AdminSessionBackup
    applyBackupValue(authStorageKeys.accessToken, backup.accessToken)
    applyBackupValue(authStorageKeys.refreshToken, backup.refreshToken)
    applyBackupValue(authStorageKeys.expiresAt, backup.expiresAt)
    applyBackupValue(authStorageKeys.user, backup.user)
    applyBackupValue(authStorageKeys.activeOrganizationId, backup.activeOrganizationId)
    return true
  } catch {
    return false
  }
}
